/**
 * Cockpit calendar view components - WeekGrid and MonthGrid.
 * The day-by-hour view lives in CockpitPage; these are the zoomed-out views.
 */
import React from 'react';
import { CardSkeleton } from '../../components/ui/Skeleton.js';

function centralOffset() {
  const now = new Date();
  const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
  const central = new Date(utc.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  return Math.round((utc.getTime() - central.getTime()) / 60000);
}

function dayKey(d: Date): string {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

export interface WeekTechCell { hours: number; count: number; }

export function bookedHoursFor(blocks: Array<{ technicianId: string; startsAt: string; endsAt: string }>, techId: string, dayStart: Date, dayEnd: Date): WeekTechCell {
  let hours = 0;
  let count = 0;
  for (const b of blocks) {
    if (b.technicianId !== techId) continue;
    const s = new Date(b.startsAt).getTime();
    const e = new Date(b.endsAt).getTime();
    const overlap = Math.min(e, dayEnd.getTime()) - Math.max(s, dayStart.getTime());
    if (overlap > 0) {
      hours += overlap / 3600000;
      count += 1;
    }
  }
  return { hours: Math.round(hours * 10) / 10, count };
}

export function WeekGrid({ techs, blocks, weekStart, decisions, onSchedule, onPickDay, oncallSlots }: {
  techs: Array<{ techId: string; name: string; weekBookedHours: number; capacityHours: number; utilization: number }>;
  blocks: Array<{ technicianId: string; startsAt: string; endsAt: string }>;
  weekStart: Date;
  decisions: Array<{ id: string; candidates: Array<{ techId: string; proposedStart?: string; proposedEnd?: string }> }>;
  onSchedule: (decisionId: string, techId: string, startsAt: string, durationMinutes: number) => void;
  onPickDay: (d: Date) => void;
  oncallSlots?: Array<{ techId: string; startsAt: string; endsAt: string; rotation: string }>;
}) {
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });
  const todayKey = dayKey(new Date());
  const wkEnd = new Date(weekStart.getTime() + 7 * 86400000);

  function dropToCell(e: React.DragEvent<HTMLDivElement>, techId: string, day: Date) {
    e.preventDefault();
    const decisionId = e.dataTransfer.getData('text/dispatch-decision');
    if (!decisionId) return;
    const decision = decisions.find(d => d.id === decisionId);
    if (!decision) return;
    const proposed = decision.candidates.find(c => c.techId === techId);
    let durationMinutes = 60;
    if (proposed?.proposedStart && proposed?.proposedEnd) {
      durationMinutes = Math.max(15, Math.min(480, Math.round((new Date(proposed.proposedEnd).getTime() - new Date(proposed.proposedStart).getTime()) / 60000)));
    }
    const startsAt = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0).toISOString();
    onSchedule(decisionId, techId, startsAt, durationMinutes);
  }

  return (
    <div className="min-w-[720px]">
      <div className="sticky top-0 z-10 flex bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))]">
        <div className="w-44 shrink-0 px-3 py-1.5 text-[10px] font-semibold uppercase text-[hsl(var(--content-subtle))] border-r border-[hsl(var(--border))]">Tech</div>
        {days.map(d => (
          <button key={dayKey(d)} onClick={() => onPickDay(d)}
            className={'flex-1 px-1 py-1.5 text-[10px] font-semibold text-center border-r border-[hsl(var(--border-subtle))] hover:bg-[hsl(var(--surface-subtle))] ' + (dayKey(d) === todayKey ? 'text-brand-600' : 'text-[hsl(var(--content-subtle))]')}>
            {d.toLocaleDateString('en-US', { weekday: 'short' })} {d.getDate()}
          </button>
        ))}
        <div className="w-20 shrink-0 px-2 py-1.5 text-[10px] font-semibold uppercase text-center text-[hsl(var(--content-subtle))]">Week</div>
      </div>
      {techs.map(tech => (
        <div key={tech.techId} className="flex border-b border-[hsl(var(--border-subtle))] h-11">
          <div className="w-44 shrink-0 px-3 py-1.5 border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
            <div className="flex items-center gap-1 min-w-0">
              <p className="text-xs font-medium text-[hsl(var(--content))] truncate">{tech.name}</p>
              {(oncallSlots ?? []).some(s => s.techId === tech.techId && new Date(s.startsAt) < wkEnd && new Date(s.endsAt) > weekStart) && (
                <span className="shrink-0 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[9px] font-semibold">On-Call</span>
              )}
            </div>
          </div>
          {days.map(d => {
            const ds = new Date(d); ds.setHours(0, 0, 0, 0);
            const de = new Date(ds.getTime() + 86400000);
            const cell = bookedHoursFor(blocks, tech.techId, ds, de);
            const oncallNow = (oncallSlots ?? []).some(s => s.techId === tech.techId && new Date(s.startsAt) < de && new Date(s.endsAt) > ds);
            const intensity = Math.min(cell.hours / 8, 1);
            return (
              <div key={dayKey(d)}
                title={cell.hours > 0 ? cell.hours + 'h booked / ' + cell.count + ' block' + (cell.count === 1 ? '' : 's') : 'Free - drop to schedule at 9:00 AM'}
                onClick={() => onPickDay(d)}
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                onDrop={(e) => dropToCell(e, tech.techId, d)}
                className={(oncallNow ? 'shadow-[inset_3px_0_0_0_rgb(245_158_11)] ' : '') + 'flex-1 border-r border-[hsl(var(--border-subtle))] cursor-pointer flex items-center justify-center transition-colors hover:ring-1 hover:ring-inset hover:ring-[hsl(var(--border-focus))]'}
                style={cell.hours > 0 ? { backgroundColor: 'hsl(221 83% 53% / ' + (0.07 + intensity * 0.45).toFixed(2) + ')' } : undefined}>
                <span className={'text-[11px] font-medium ' + (intensity > 0.55 ? 'text-[hsl(var(--content))]' : 'text-[hsl(var(--content-subtle))]')}>
                  {cell.hours > 0 ? cell.hours + 'h' : '—'}
                </span>
              </div>
            );
          })}
          <div className="w-20 shrink-0 flex items-center justify-center border-r border-[hsl(var(--border-subtle))]">
            <span className={'text-[11px] font-semibold tabular-nums ' + (tech.utilization > 0.85 ? 'text-red-500' : tech.utilization > 0.6 ? 'text-amber-500' : 'text-[hsl(var(--content-subtle))]')}>
              {tech.weekBookedHours}/{tech.capacityHours}h
            </span>
          </div>
        </div>
      ))}
      <p className="px-3 py-2 text-[10px] text-[hsl(var(--content-placeholder))]">Click a cell to open the day view / drop a queue card to schedule at 9:00 AM / right column = booked/capacity hours</p>
    </div>
  );
}

export function MonthGrid({ monthDate, onPickDay }: { monthDate: Date; onPickDay: (d: Date) => void }) {
  const [weeks, setWeeks] = React.useState<Array<{ weekStart: string; data: { items: Array<{ days: Array<{ date: string; bookedHours: number; timeOffHours: number; blocks: number }> }> } | null }> | null>(null);

  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  React.useEffect(() => {
    let cancelled = false;
    setWeeks(null);
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const mondayOf = (x: Date) => { const d = new Date(x); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d; };
    const starts: Date[] = [];
    for (let m = mondayOf(first); m <= mondayOf(last); m = new Date(m.getTime() + 7 * 86400000)) {
      starts.push(new Date(m));
    }
    Promise.all(starts.map(async (m) => {
      const key = m.getFullYear() + '-' + String(m.getMonth() + 1).padStart(2, '0') + '-' + String(m.getDate()).padStart(2, '0');
      try {
        const res = await fetch('/api/v1/schedule?weekStart=' + key + '&tz=' + centralOffset());
        if (!res.ok) return { weekStart: key, data: null };
        return { weekStart: key, data: await res.json() };
      } catch {
        return { weekStart: key, data: null };
      }
    })).then((w) => { if (!cancelled) setWeeks(w); });
    return () => { cancelled = true; };
  }, [year, month]);

  const firstDay = new Date(year, month, 1);
  const gridStart = new Date(year, month, 1 - ((firstDay.getDay() + 6) % 7));
  const cells = Array.from({ length: 42 }, (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  const todayKey = dayKey(new Date());

  function dayTotal(d: Date): { hours: number; blocks: number } {
    if (!weeks) return { hours: 0, blocks: 0 };
    const key = dayKey(d);
    let hours = 0;
    let blocks = 0;
    for (const w of weeks) {
      if (!w.data) continue;
      for (const item of w.data.items) {
        const cell = item.days.find(x => x.date === key);
        if (cell) { hours += cell.bookedHours; blocks += cell.blocks; }
      }
    }
    return { hours: Math.round(hours * 10) / 10, blocks };
  }

  return (
    <div className="p-4">
      {!weeks ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} lines={1} />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-7 mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(w => (
              <div key={w} className="py-1 text-center text-[10px] font-semibold uppercase text-[hsl(var(--content-subtle))]">{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map(d => {
              const inMonth = d.getMonth() === month;
              const total = dayTotal(d);
              const intensity = Math.min(total.hours / 40, 1);
              return (
                <button key={dayKey(d)} onClick={() => onPickDay(d)}
                  className={'h-20 rounded-lg border p-1.5 text-left transition-colors ' + (inMonth ? 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:border-[hsl(var(--border-focus))]' : 'border-transparent opacity-40')}
                  style={inMonth && total.hours > 0 ? { backgroundColor: 'hsl(221 83% 53% / ' + (0.05 + intensity * 0.4).toFixed(2) + ')' } : undefined}>
                  <span className={'text-xs font-semibold ' + (dayKey(d) === todayKey ? 'text-brand-600' : 'text-[hsl(var(--content))]')}>{d.getDate()}</span>
                  {inMonth && total.hours > 0 && (
                    <span className="block mt-1 text-[10px] text-[hsl(var(--content-subtle))] leading-tight">{total.hours}h / {total.blocks} blk</span>
                  )}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] text-[hsl(var(--content-placeholder))]">Team totals per day (booked hours across technicians) / click a day to open the day view</p>
        </>
      )}
    </div>
  );
}
